import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Consents } from '@/api/entities';
import { Loader2, Download, Eye } from 'lucide-react';

const PAGE_SIZE = 50;

export default function ConsentRegistrySettings() {
  const [filters, setFilters] = useState({
    from: '', to: '', document_type: '', email: '', phone: '', search: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewer, setViewer] = useState(null); // selected consent record

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await Consents.list(appliedFilters, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      setData(res || { items: [], total: 0 });
    } catch (e) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  useEffect(() => { reload(); }, [reload]);

  const apply = () => { setPage(0); setAppliedFilters(filters); };
  const reset = () => {
    const empty = { from: '', to: '', document_type: '', email: '', phone: '', search: '' };
    setFilters(empty); setAppliedFilters(empty); setPage(0);
  };

  const downloadExport = async (format) => {
    try {
      const blob = await Consents.exportBlob(appliedFilters, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consents.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Не удалось скачать: ${e.message}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  return (
    <Card className="elegant-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <CardTitle>Реестр согласий</CardTitle>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadExport('csv')}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport('json')}>
            <Download className="h-4 w-4 mr-1" /> JSON
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <Label>С</Label>
            <Input type="datetime-local" value={filters.from} onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))} />
          </div>
          <div>
            <Label>По</Label>
            <Input type="datetime-local" value={filters.to} onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))} />
          </div>
          <div>
            <Label>Тип документа</Label>
            <Input value={filters.document_type} onChange={(e) => setFilters(f => ({ ...f, document_type: e.target.value }))} placeholder="agreement" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={filters.email} onChange={(e) => setFilters(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <Label>Телефон</Label>
            <Input value={filters.phone} onChange={(e) => setFilters(f => ({ ...f, phone: e.target.value }))} />
          </div>
          <div>
            <Label>Поиск (имя/email/тел.)</Label>
            <Input value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>
        </div>
        <div className="flex gap-2 mb-4">
          <Button onClick={apply}>Применить</Button>
          <Button variant="outline" onClick={reset}>Сбросить</Button>
        </div>

        {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
        {loading && <div className="flex items-center text-sm text-gray-500"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Загрузка...</div>}

        {/* Desktop: таблица. Mobile: вертикальные карточки. */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left p-2">Дата</th>
                <th className="text-left p-2">Субъект</th>
                <th className="text-left p-2">Документ</th>
                <th className="text-left p-2">Версия</th>
                <th className="text-left p-2">IP</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(c => (
                <tr key={c.id} className="border-b">
                  <td className="p-2 whitespace-nowrap">{new Date(c.granted_at).toLocaleString()}</td>
                  <td className="p-2">
                    <div>{c.subject_name || <span className="text-gray-400">—</span>}</div>
                    <div className="text-xs text-gray-500 break-all">{c.subject_email || c.subject_phone || ''}</div>
                  </td>
                  <td className="p-2">
                    <div>{c.document_name || <span className="text-gray-400">—</span>}</div>
                    <div className="text-xs text-gray-500"><code>{c.document_type}</code></div>
                  </td>
                  <td className="p-2"><Badge variant="secondary">{c.document_version || '—'}</Badge></td>
                  <td className="p-2 font-mono text-xs break-all">{c.ip_address || '—'}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setViewer(c)}>
                      <Eye className="h-3 w-3 mr-1" /> Детали
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && !data.items.length && (
                <tr><td colSpan={6} className="p-4 text-center text-gray-500">Нет записей по текущему фильтру</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="sm:hidden space-y-2">
          {data.items.map(c => (
            <div key={c.id} className="border rounded-lg p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <span className="text-xs text-gray-500">{new Date(c.granted_at).toLocaleString()}</span>
                <Badge variant="secondary">{c.document_version || '—'}</Badge>
              </div>
              <div className="font-medium break-words">{c.subject_name || '—'}</div>
              <div className="text-xs text-gray-600 break-all">{c.subject_email || c.subject_phone || ''}</div>
              <div className="mt-1 break-words">
                {c.document_name || '—'} <span className="text-xs text-gray-500">(<code>{c.document_type}</code>)</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 font-mono break-all">{c.ip_address || '—'}</span>
                <Button size="sm" variant="ghost" onClick={() => setViewer(c)}>
                  <Eye className="h-3 w-3 mr-1" /> Детали
                </Button>
              </div>
            </div>
          ))}
          {!loading && !data.items.length && (
            <div className="p-4 text-center text-gray-500 text-sm">Нет записей по текущему фильтру</div>
          )}
        </div>

        <div className="flex items-center justify-between mt-3 text-sm">
          <span>Всего: {data.total || 0}</span>
          <div className="flex gap-2 items-center">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Назад</Button>
            <span>Стр. {page + 1} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Вперёд</Button>
          </div>
        </div>

        {/* Детальный просмотр */}
        <Dialog open={!!viewer} onOpenChange={(open) => !open && setViewer(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Согласие #{viewer?.id?.slice(0, 8)}</DialogTitle>
            </DialogHeader>
            {viewer && (
              <div className="space-y-2 text-sm break-words">
                <div><b>Дата:</b> {new Date(viewer.granted_at).toLocaleString()}</div>
                <div className="break-all"><b>Subject:</b> {viewer.subject_name || '—'} {viewer.subject_email ? `<${viewer.subject_email}>` : ''} {viewer.subject_phone || ''}</div>
                <div><b>Документ:</b> {viewer.document_name} (<code>{viewer.document_type}</code>) v{viewer.document_version || '—'}</div>
                <div className="break-all"><b>document_id:</b> <code className="text-xs">{viewer.document_id}</code></div>
                <div className="break-all"><b>submission_id:</b> <code className="text-xs">{viewer.submission_id}</code></div>
                <div className="break-all"><b>IP:</b> {viewer.ip_address || '—'}</div>
                <div className="break-words"><b>User-Agent:</b> {viewer.user_agent || '—'}</div>
                {viewer.metadata && (
                  <div>
                    <b>Metadata:</b>
                    <pre className="bg-slate-50 p-2 rounded text-xs whitespace-pre-wrap break-all">{JSON.stringify(viewer.metadata, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
